// Shared manager runtime leaves: logging, hashing, and child-process helpers.
//
// These are the lowest layer of the manager daemon — they depend only on the
// Node standard library and on each other, never on any other manager module.
// Keeping them in a dependency-free leaf is what lets the rest of the manager
// (`scheduler/`, `kanban/`, `delivery/`, `lifecycle/`, and `manager-worker.ts`
// itself) import them without creating import cycles. See ADR-0011.
//
// `log()` is intentionally here, not in lifecycle: its only dependencies are a
// log-path constant and the Node fs API, so treating it as a leaf is accurate
// (an earlier draft of ADR-0011 deferred it to lifecycle, which would have
// forced every importer of `log` to depend on lifecycle — a cycle factory).

import { createHash } from 'node:crypto';
import { readFileSync, appendFileSync, mkdirSync, chmodSync, existsSync } from 'node:fs';
import { join, dirname } from 'node:path';
import { homedir } from 'node:os';

// Redact secret-shaped tokens from log messages before they hit any durable
// sink. Applied to both stderr and the on-disk mirror — stderr gets captured
// by journald / cloud-init / supervisor logs in production, so "raw on
// stderr" is not actually safe. No raw-secret escape hatch: if you need to
// inspect a live token, read it from its source of truth (env, secrets
// manager) rather than scraping it from a log stream.
//
// Patterns:
//   - host API keys        tlk_…
//   - Slack tokens         xox[baprs]-…
//   - Anthropic keys       sk-ant-…
//   - Telegram bot tokens  <digits>:<base64url>
//   - Bearer JWTs          Bearer …
//   - env-var assignments  *_TOKEN=, *_SECRET=, *_API_KEY=, *_PASSWORD=
//     (broad shape match so future secrets we forget to enumerate are
//     still redacted).
export function redactForDiskLog(value: string): string {
  try {
    return value
      .replace(/\b(Bearer\s+)[A-Za-z0-9._-]+\b/gi, '$1[REDACTED]')
      .replace(/\bxox[baprs]-[A-Za-z0-9-]+\b/g, '[REDACTED-SLACK]')
      .replace(/\btlk_[A-Za-z0-9._-]+\b/g, '[REDACTED-HOST]')
      .replace(/\bsk-ant-[A-Za-z0-9_-]+\b/g, '[REDACTED-ANTHROPIC]')
      .replace(/\b\d{8,12}:[A-Za-z0-9_-]{30,}\b/g, '[REDACTED-TELEGRAM]')
      .replace(
        /\b([A-Z0-9_]*(?:TOKEN|SECRET|API[_-]?KEY|PASSWORD)[A-Z0-9_]*)=(?:"[^"\r\n]*"|'[^'\r\n]*'|[^\s\r\n]+)/gi,
        '$1=[REDACTED]',
      );
  } catch {
    return '[REDACTED]';
  }
}

// Path initialized lazily on first log() call so we don't need a second
// explicit init step. The manager always has access to ~/.augmented.
let managerLogPath: string | null = null;
// Whether log() should mirror to manager.log. If the first appendFileSync
// throws, flip to false and stay stderr-only for the rest of this worker
// process — no per-poll append-failure spam, no retry. The next worker
// (after a respawn) starts fresh with managerLogWritable=true and gets
// its own one-shot opportunity. The first failure also writes a single
// reason line to stderr so operators see what's wrong.
let managerLogWritable = true;

export function log(msg: string): void {
  const ts = new Date().toISOString();
  const safeMsg = redactForDiskLog(msg);
  const line = `[manager-worker ${ts}] ${safeMsg}\n`;

  // ENG-4658: Write directly to ~/.augmented/manager.log via O_APPEND
  // rather than relying on the supervisor having inherited a redirected
  // stderr fd. The previous behaviour assumed `agt manager start
  // --supervise` was launched with `nohup ... >> manager.log 2>&1` (or
  // a launchd/systemd unit doing the equivalent), and the spawned
  // worker would inherit those file descriptors. When systemd respawns
  // the supervisor without that shell redirection — or any time
  // logrotate moves the file out from under an inherited fd — the
  // worker's stderr silently goes to the journal / /dev/null and
  // operators see manager.log freeze.
  //
  // Direct append makes the worker self-sufficient: it always writes to
  // the configured path with O_APPEND, regardless of how stdio was set
  // up by whichever process spawned us. We still echo to stderr so
  // foreground runs (interactive `agt manager start` without
  // `--supervise`) and any supervisor that DOES capture stdio get the
  // same lines for free.
  if (!managerLogPath) {
    try {
      managerLogPath = join(homedir(), '.augmented', 'manager.log');
      mkdirSync(dirname(managerLogPath), { recursive: true });
      if (existsSync(managerLogPath)) {
        chmodSync(managerLogPath, 0o600);
      }
    } catch { /* non-fatal — first-touch perm hardening is best-effort */ }
  }
  let appendedToFile = false;
  if (managerLogPath && managerLogWritable) {
    try {
      // O_APPEND on every call: cheap (no persistent fd to drift), and
      // each append is atomic for writes <= PIPE_BUF on Linux which
      // these single-line entries always are.
      appendFileSync(managerLogPath, line, { encoding: 'utf-8', mode: 0o600 });
      appendedToFile = true;
    } catch (err) {
      // First failure flips the gate so we don't spam appendFileSync
      // attempts every poll. Print the reason to stderr once so an
      // operator knows the on-disk log is gone.
      managerLogWritable = false;
      process.stderr.write(
        `[manager-worker ${ts}] [log] manager.log append failed; falling back to stderr-only: ${(err as Error).message}\n`,
      );
    }
  }
  // Echo to stderr only when:
  //   - we couldn't write to the file (fallback diagnostic), OR
  //   - stderr is a real TTY (foreground operator running interactively)
  // Under supervision, launchd / systemd / nohup all redirect stderr at
  // manager.log already (apps/cli/src/__tests__/manager-supervisor.test.ts
  // confirms launchd routes both StandardOutPath and StandardErrorPath
  // there). With the direct append above, an unconditional stderr write
  // would land each line in the file twice — once via our append, once
  // via the supervisor's redirect. CodeRabbit catch on PR #619.
  if (!appendedToFile || process.stderr.isTTY === true) {
    process.stderr.write(line);
  }
}

export function sha256(content: string): string {
  return createHash('sha256').update(content, 'utf8').digest('hex');
}

export function hashFile(filePath: string): string | null {
  try {
    const content = readFileSync(filePath, 'utf-8');
    return sha256(content);
  } catch {
    return null;
  }
}

export async function execFilePromise(cmd: string, args: string[]): Promise<{ stdout: string; stderr: string }> {
  const { execFile: ef } = await import('node:child_process');
  return new Promise((resolve, reject) => {
    ef(cmd, args, { timeout: 15_000 }, (err, stdout, stderr) => {
      if (err) reject(err);
      else resolve({ stdout, stderr });
    });
  });
}

/**
 * Rejection shape for non-zero exits. Includes stdout AND stderr so
 * callers can log both — some tools (Claude CLI in particular) write
 * startup errors to stdout and leave stderr empty, which makes
 * stderr-only logging useless for debugging.
 */
export class ChildProcessError extends Error {
  public readonly code: number | null;
  public readonly stdout: string;
  public readonly stderr: string;
  constructor(code: number | null, stdout: string, stderr: string) {
    const stderrSnippet = stderr.trim().slice(0, 500);
    const stdoutSnippet = stdout.trim().slice(0, 500);
    // Prefer stderr in the message; fall back to stdout when stderr is empty.
    const detail = stderrSnippet || stdoutSnippet || '(no output)';
    super(`Exit code ${code}: ${detail}`);
    this.name = 'ChildProcessError';
    this.code = code;
    this.stdout = stdout;
    this.stderr = stderr;
  }
}

export async function execFilePromiseLong(
  cmd: string,
  args: string[],
  opts?: {
    cwd?: string;
    timeout?: number;
    stdin?: 'ignore';
    env?: NodeJS.ProcessEnv;
    /**
     * ENG-5865 — fired once with the child's pid as soon as the OS hands it
     * back. Used by the claude -p call sites to register the spawn with the
     * pid-tracker so the boot-side reaper can clean up if the manager dies
     * mid-execution. Pure callbacks; doesn't change the await semantics.
     */
    onSpawn?: (pid: number) => void;
    onExit?: (pid: number) => void;
  },
): Promise<{ stdout: string; stderr: string }> {
  const { spawn: sp } = await import('node:child_process');
  return new Promise((resolve, reject) => {
    const child = sp(cmd, args, {
      cwd: opts?.cwd,
      stdio: [opts?.stdin === 'ignore' ? 'ignore' : 'pipe', 'pipe', 'pipe'],
      ...(opts?.env ? { env: opts.env } : {}),
    });
    if (opts?.onSpawn && typeof child.pid === 'number') {
      try { opts.onSpawn(child.pid); } catch { /* observer crash is not the child's problem */ }
    }
    let stdout = '';
    let stderr = '';
    child.stdout?.on('data', (d: Buffer) => { stdout += d.toString(); });
    child.stderr?.on('data', (d: Buffer) => { stderr += d.toString(); });
    const timer = setTimeout(() => { child.kill(); reject(new Error(`Timed out after ${opts?.timeout ?? 120_000}ms`)); }, opts?.timeout ?? 120_000);
    child.on('close', (code) => {
      clearTimeout(timer);
      if (opts?.onExit && typeof child.pid === 'number') {
        try { opts.onExit(child.pid); } catch { /* see above */ }
      }
      if (code !== 0) reject(new ChildProcessError(code, stdout, stderr));
      else resolve({ stdout, stderr });
    });
    child.on('error', (err) => { clearTimeout(timer); reject(err); });
  });
}
